import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import client from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Camera, CheckCircle, ArrowRight, Gift, Crown, VideoCamera, Sparkle, House } from "@phosphor-icons/react";
import { toast } from "sonner";
import { celebrate } from "../lib/celebrate";
import { money, reeltyPackagePrice, REELTY_PRICING } from "../lib/helpers";
import { BrandMark } from "../components/BrandMark";
import { QuestProgress } from "../components/ui/quest-progress";
import { captureRef } from "../lib/referral";

const REF_STEPS = [{ k: "you", t: "You" }, { k: "addr", t: "Address" }, { k: "pkg", t: "Package" }, { k: "done", t: "Confirm" }];

export default function AgentReferralLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    street: "", city: "", state: "", zip: "",
    shoot_date: "", package_type: "All-Inclusive", drone: false, interior: true,
    agent_name: "", agent_email: "", agent_phone: "", notes: "",
  });

  useEffect(() => {
    captureRef(slug);
    client.get(`/public/agent/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pkg = data?.packages?.find((p) => p.type === form.package_type);
  const price = pkg ? reeltyPackagePrice(form.package_type, form.interior) : 0;

  const submit = async () => {
    if (!form.agent_name || !form.agent_email) return toast.error("Please add your name and email");
    if (!form.street || !form.city || !form.state || !form.zip) return toast.error("Please add the full property address");
    if (!form.package_type) return toast.error("Please choose a package");
    setSaving(true);
    try {
      const r = await client.post(`/public/agent/${slug}/book`, form);
      celebrate();
      setDone(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not book — please try again"); }
    finally { setSaving(false); }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-[#5C5A58]" data-testid="referral-notfound">This referral page isn't available.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#4A0000] font-black text-2xl">REELTY</div>;

  if (done) return (
    <div className="min-h-screen bg-[#FBFAF7] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-[#E9DDC6] rounded-md p-8 text-center shadow-premium" data-testid="referral-success">
        <CheckCircle size={48} weight="duotone" className="text-[#2e7d32] mx-auto mb-4" />
        <h1 className="font-display text-3xl text-[#4A0000] mb-2">You're booked!</h1>
        <p className="text-[#5C5A58] text-sm">{done.message}</p>
        <p className="text-xs text-[#9A968E] mt-4">Referred by {done.referred_by}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBFAF7]" data-testid="agent-referral-landing">
      <div className="bg-[#4A0000] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <BrandMark logoSize={26} textClass="text-white" textSize="text-xl" />
          <span className="eyebrow text-white/50 flex items-center gap-1"><Gift size={14} weight="duotone" />Referral booking</span>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <p className="eyebrow text-[#B08D57]">{data.company || "Reelty"}</p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#4A0000] leading-tight">Book a shoot with Reelty</h1>
        <p className="text-[#5C5A58] mt-2">Referred by <span className="font-medium text-[#4A0000]">{data.name}</span>. Fill in the details below and we'll take it from there.</p>

        <div className="mt-8 grid gap-5 lux-card rounded-[5px] p-6">
          <QuestProgress steps={REF_STEPS} step={!form.agent_email ? 0 : !form.street ? 1 : 3} testid="ref-quest" className="mb-1" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Your name</Label><Input data-testid="ref-name" value={form.agent_name} onChange={(e) => set("agent_name", e.target.value)} placeholder="Jane Agent" /></div>
            <div><Label>Email</Label><Input data-testid="ref-email" type="email" value={form.agent_email} onChange={(e) => set("agent_email", e.target.value)} placeholder="you@brokerage.com" /></div>
          </div>
          <div><Label>Phone (optional)</Label><Input data-testid="ref-phone" value={form.agent_phone} onChange={(e) => set("agent_phone", e.target.value)} placeholder="(555) 123-4567" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Property street address</Label><Input data-testid="ref-street" value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="123 Main St" /></div>
            <div><Label>City</Label><Input data-testid="ref-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Charlotte" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>State</Label><Input data-testid="ref-state" value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase())} placeholder="NC" maxLength={2} /></div>
              <div><Label>ZIP</Label><Input data-testid="ref-zip" value={form.zip} onChange={(e) => set("zip", e.target.value)} placeholder="28202" /></div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div data-testid="ref-package-picker">
              <Label>Your package</Label>
              {form.package_type !== "Photo Only" ? (
                <div className="relative rounded-xl border p-4 mt-1.5 overflow-hidden border-[#4A0000] bg-[#4A0000] text-white" data-testid="ref-package">
                  <span className="absolute top-0 right-0 bg-[#B08D57] text-[#1A1715] text-[10px] font-bold tracking-[0.12em] uppercase px-2.5 py-1 rounded-bl-lg">Most booked</span>
                  <div className="flex items-center gap-2.5">
                    <span className="grid place-items-center w-9 h-9 rounded-full bg-[#B08D57]/20"><Camera size={20} weight="fill" className="text-[#B08D57]" /></span>
                    <div><div className="font-display text-base leading-none">Penthouse Package</div><div className="text-[10px] tracking-[0.16em] uppercase mt-1 text-[#B08D57]">All-Inclusive</div></div>
                  </div>
                  <div className="mt-3 grid gap-1.5 text-xs text-white/85">
                    <span className="flex items-center gap-2"><CheckCircle size={13} weight="fill" className="text-[#B08D57] shrink-0" />Interior &amp; exterior photos</span>
                    <span className="flex items-center gap-2"><VideoCamera size={13} weight="fill" className="text-[#B08D57] shrink-0" />Cinematic video</span>
                    <span className="flex items-center gap-2"><Sparkle size={13} weight="fill" className="text-[#B08D57] shrink-0" />Virtual staging</span>
                  </div>
                  <button type="button" data-testid="ref-only-photos" onClick={() => set("package_type", "Photo Only")}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-white/70 hover:text-white underline decoration-dotted underline-offset-4">
                    <House size={12} weight="duotone" />Only need photos? — {money(REELTY_PRICING.photoOnly)}
                  </button>
                </div>
              ) : (
                <div className="relative rounded-xl border p-4 mt-1.5 border-[#4A0000] bg-[#4A0000] text-white" data-testid="ref-package">
                  <div className="flex items-center gap-2.5">
                    <span className="grid place-items-center w-9 h-9 rounded-full bg-[#B08D57]/20"><House size={20} weight="fill" className="text-[#B08D57]" /></span>
                    <div><div className="font-display text-base leading-none">Photo Only</div><div className="text-[11px] mt-1 text-white/70">30–50 pro photos</div></div>
                  </div>
                  <button type="button" data-testid="ref-back-penthouse" onClick={() => set("package_type", "All-Inclusive")}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-[#B08D57] hover:text-white font-medium">
                    <Crown size={12} weight="fill" />Get video + staging → Penthouse Package
                  </button>
                </div>
              )}
            </div>
            <div><Label>Preferred date</Label><Input data-testid="ref-date" type="date" value={form.shoot_date} onChange={(e) => set("shoot_date", e.target.value)} /></div>
          </div>
          <div className="rounded-lg border border-[#B08D57]/45 bg-[#B08D57]/10 px-3 py-2.5 flex items-start gap-2.5" data-testid="ref-partner-callout">
            <Crown size={18} weight="fill" className="text-[#B08D57] mt-0.5 shrink-0" />
            <p className="text-xs leading-relaxed text-[#7a5c1a]"><b className="text-[#4A0000]">First shoot: 360° tour + drone free</b> — a Penthouse Partner welcome, 1st month on us.</p>
          </div>
          {form.package_type === "All-Inclusive" && (
            <label className="flex items-center gap-2 text-sm text-[#5C5A58] cursor-pointer" data-testid="ref-interior-label">
              <input type="checkbox" data-testid="ref-interior" checked={!form.interior} onChange={(e) => set("interior", !e.target.checked)} className="accent-[#4A0000] w-4 h-4" />
              Skip interior — drone / exterior only <span className="text-[#2e7d32] font-medium">(save {money(135)})</span>
            </label>
          )}
          <div><Label>Notes (optional)</Label><Input data-testid="ref-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Gate code, access notes, etc." /></div>
          <div className="flex items-center justify-between border-t border-[#F0ECE2] pt-4">
            <span className="text-sm text-[#5C5A58]">Estimated total</span>
            <span className="font-display text-2xl text-[#4A0000]" data-testid="ref-total">{money(price)}</span>
          </div>
          <Button data-testid="ref-submit" disabled={saving} onClick={submit} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white w-full">
            <Camera size={18} className="mr-2" weight="duotone" />{saving ? "Booking…" : "Book my shoot"}<ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
